import React, { useState, useEffect } from "react";
import { ButtonConfig, Action } from "./InterfaceButtonConfig";
import { Button, Flex, Input, message, Select } from "antd";
import {
  CheckCircleOutlined,
  CheckOutlined,
  CopyOutlined,
  DeleteOutlined,
  DownOutlined,
  EditOutlined,
  ExportOutlined,
  MinusCircleOutlined,
  PlusCircleOutlined,
  PlusOutlined,
  ReloadOutlined,
  UpOutlined,
} from "@ant-design/icons";

// Ant Design Select Option type
const { Option } = Select;

const ReportActionConfig: React.FC = () => {
  const generateId = () =>
    Date.now().toString() + Math.random().toString(36).substring(2, 6); // NOSONAR: Non-secure random number generator is safe for generating temporary IDs
  const [buttons, setButtons] = useState<ButtonConfig[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [jsonInput, setJsonInput] = useState("");

  // Initialize with empty state
  useEffect(() => {
    setButtons([]); // Start with an empty state for buttons
    setJsonInput(""); // Empty JSON input
  }, []);

  const selected = buttons.find((btn) => btn.id === selectedId) || null;

  const loadJson = () => {
    try {
      const parsed = JSON.parse(jsonInput);
      if (!Array.isArray(parsed)) throw new Error("Expected an array");
      const withIds = parsed.map((btn: Partial<ButtonConfig>) => ({
        id: generateId(),
        type: btn.type ?? "Button",
        mode: btn.mode ?? "Default",
        label: btn.label ?? "",
        icon: btn.icon ?? "",
        className: btn.className ?? "",
        actionType: btn.actionType ?? "",
        actions: (btn.actions ?? []).map((act: Partial<Action>) => ({
          actionID: act.actionID ?? "", // Corrected from actionId to actionID
          execType: act.execType ?? "",
          execData: act.execData ?? "",
          execParams: act.execParams ?? [],
        })),
      }));
      setButtons(withIds);
      setSelectedId(null);
    } catch (err: any) {
      message.error("Invalid JSON format: " + err.message);
    }
  };

  // Update Query JSON
  const updateQueryJsonInput = () => {
    try {
      const parseddata = JSON.parse(jsonInput);
      const stringified = JSON.stringify(parseddata);
      const query = `Update Report SET ReportActionConfig = '${stringified}' Where Id = ??`;
      navigator.clipboard
        .writeText(query)
        .then(() => {
          message.success("JSON copied to clipboard!");
        })
        .catch(() => {
          message.error("Failed to copy text!");
        });
    } catch {
      message.error("Invalid JSON format");
      return jsonInput; // Return original if parsing fails
    }
  };

  const updateSelected = (updated: ButtonConfig) => {
    setButtons((btns) =>
      btns.map((btn) => (btn.id === updated.id ? updated : btn))
    );
  };

  const addButton = () => {
    const newBtn: ButtonConfig = {
      id: generateId(),
      type: "Button",
      mode: "",
      label: "",
      icon: "",
      className: "",
      actionType: "",
      actions: [],
    };
    setButtons([...buttons, newBtn]);
    setSelectedId(newBtn.id);
  };

  const deleteButton = (id: string) => {
    setButtons((btns) => btns.filter((b) => b.id !== id));
    if (selectedId === id) setSelectedId(null);
  };

  // validate JSON
  const validateJson = (json: string) => {
    try {
      const parsed = JSON.parse(json);
      message.success("JSON is correct format!");
      return JSON.stringify(parsed, null, 2); // Pretty print with 2 spaces
    } catch {
      message.error("Invalid JSON format");
      return json; // Return original if parsing fails
    }
  };

  // beautify JSON
  const beautifyJson = (json: string) => {
    try {
      const parsed = JSON.parse(json);
      return JSON.stringify(parsed, null, 2); // Pretty print with 2 spaces
    } catch {
      message.error("Invalid JSON format for beautification");
      return json; // Return original if parsing fails
    }
  };

  // minify JSON
  const minifyJson = (json: string) => {
    try {
      const parsed = JSON.parse(json);
      return JSON.stringify(parsed); // Minified JSON
    } catch {
      message.error("Invalid JSON format for minification");
      return json; // Return original if parsing fails
    }
  };

  useEffect(() => {
    if (buttons.length === 0) {
      setJsonInput(""); // or setJsonInput(null); if you prefer null
    } else {
      const cleaned = buttons.map(({ id, ...rest }) => rest);
      setJsonInput(JSON.stringify(cleaned, null, 2));
    }
  }, [buttons]);

  // Handle Action Mode Change
  const handleActionModeChange = (mode: string) => {
    if (selected) {
      updateSelected({ ...selected, mode });
    }
  };

  // Handle Exec Type Change
  const handleExecTypeChange = (execType: string, index: number) => {
    if (selected) {
      const updatedActions = [...selected.actions];
      updatedActions[index] = { ...updatedActions[index], execType };
      updateSelected({ ...selected, actions: updatedActions });
    }
  };

  // Move buttons up
  const moveButtonUp = (id: string) => {
    const index = buttons.findIndex((btns) => btns.id === id);
    if (index > 0) {
      const newButtons = [...buttons];
      const [movedButton] = newButtons.splice(index, 1);
      newButtons.splice(index - 1, 0, movedButton);
      setButtons(newButtons);
    }
  };

  // Move buttons down
  const moveButtonDown = (id: string) => {
    const index = buttons.findIndex((btns) => btns.id === id);
    if (index < buttons.length - 1) {
      const newButtons = [...buttons];
      const [movedButton] = newButtons.splice(index, 1);
      newButtons.splice(index + 1, 0, movedButton);
      setButtons(newButtons);
    }
  };

  // Move Action up
  const moveActionUp = (actionIndex: number) => {
    if (actionIndex > 0) {
      const updatedActions = [...selected!.actions];
      const [movedAction] = updatedActions.splice(actionIndex, 1);
      updatedActions.splice(actionIndex - 1, 0, movedAction);
      updateSelected({ ...selected!, actions: updatedActions });
    }
  };

  // Move Action down
  const moveActionDown = (actionIndex: number) => {
    if (actionIndex < selected!.actions.length - 1) {
      const updatedActions = [...selected!.actions];
      const [movedAction] = updatedActions.splice(actionIndex, 1);
      updatedActions.splice(actionIndex + 1, 0, movedAction);
      updateSelected({ ...selected!, actions: updatedActions });
    }
  };

  // --- Helper function outside JSX ---
  function handleParamNameChange(
    actionIndex: number,
    paramIndex: number,
    newName: string
  ) {
    const updatedActions = updateActionParam(
      selected?.actions ?? [],
      actionIndex,
      paramIndex,
      newName
    );
    updateSelected({ ...selected, actions: updatedActions });
  }

  // --- Pure helper logic ---
  function updateActionParam(
    actions: any[],
    actionIndex: any,
    paramIndex: any,
    newName: any
  ) {
    return actions.map((action, idx) =>
      idx === actionIndex
        ? {
            ...action,
            execParams: updateExecParams(
              action.execParams,
              paramIndex,
              newName
            ),
          }
        : action
    );
  }

  function updateExecParams(execParams: any[], paramIndex: any, newName: any) {
    return execParams.map((param: any, idx: any) =>
      idx === paramIndex ? { ...param, name: newName } : param
    );
  }

  return (
    <div className="App">
      <h3>Report action JSON builder</h3>

      <Input.TextArea
        rows={6}
        placeholder="Paste JSON here..."
        value={jsonInput}
        onChange={(e) => setJsonInput(e.target.value)}
        style={{ resize: "none", width: "100%" }}
      />

      <div style={{ marginTop: 20, marginBottom: 8 }}>
        <Flex vertical gap="small">
          <Flex gap="small" wrap>
            <Button
              onClick={loadJson}
              color="primary"
              variant="solid"
              icon={<ReloadOutlined />}
            >
              Load JSON
            </Button>

            <Button
              onClick={() => {
                try {
                  const parseddata = JSON.parse(jsonInput);
                  console.log(parseddata);
                  navigator.clipboard.writeText(jsonInput);
                  message.success("JSON copied to clipboard!");
                } catch {
                  message.error("Invalid JSON format");
                  return jsonInput;
                }
              }}
              color="pink"
              variant="solid"
              icon={<CopyOutlined />}
            >
              Copy JSON
            </Button>

            <Button
              onClick={() => {
                const beautified = beautifyJson(jsonInput);
                setJsonInput(beautified);
                message.success("Beautified JSON Done!");
              }}
              color="purple"
              variant="solid"
              icon={<ExportOutlined />}
            >
              Beautify
            </Button>

            <Button
              onClick={() => {
                const minified = minifyJson(jsonInput);
                setJsonInput(minified);
                message.success("Minified JSON Done!");
              }}
              color="orange"
              variant="solid"
              icon={<MinusCircleOutlined />}
            >
              Minify
            </Button>

            <Button
              onClick={() => {
                const validateJsondata = validateJson(jsonInput);
                setJsonInput(validateJsondata);
              }}
              color="danger"
              variant="solid"
              icon={<CheckCircleOutlined />}
            >
              Validate JSON{" "}
            </Button>

            <Button
              onClick={updateQueryJsonInput}
              color="cyan"
              variant="solid"
              icon={<CheckOutlined />}
            >
              Copy JSON Query
            </Button>
          </Flex>
        </Flex>
      </div>

      <h3>Buttons</h3>

      {buttons.map((btn) => (
        <div
          key={btn.id}
          style={{ marginBottom: 8, padding: 6, border: "1px solid #ccc" }}
        >
          <Flex vertical gap="small">
            <Flex gap="small" wrap>
              <b>{btn.label || "Untitled"}</b> [{btn.mode}]
              <Button
                onClick={() => setSelectedId(btn.id)}
                color="primary"
                variant="dashed"
                size="small"
                icon={<EditOutlined />}
              />
              <Button
                onClick={() => deleteButton(btn.id)}
                color="danger"
                variant="dashed"
                size="small"
                icon={<DeleteOutlined />}
              />
              <Button
                onClick={() => moveButtonUp(btn.id)}
                color="geekblue"
                variant="dashed"
                size="small"
                icon={<UpOutlined />}
              />
              <Button
                onClick={() => moveButtonDown(btn.id)}
                color="magenta"
                variant="dashed"
                size="small"
                icon={<DownOutlined />}
              />
            </Flex>
          </Flex>
        </div>
      ))}

      <Button
        onClick={addButton}
        color="primary"
        variant="solid"
        icon={<PlusCircleOutlined />}
      >
        Add New Button
      </Button>

      {selected && (
        <div style={{ marginTop: 20 }}>
          <h3>Edit Button</h3>

          <table className="table table-bordered">
            <thead style={{ display: "none" }}>
              <tr>
                <th scope="col"></th>
                <th scope="col"></th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <th scope="row">Label Name</th>
                <td>
                  <Input
                    placeholder="Button label to be visible on screen"
                    value={selected.label}
                    onChange={(e) =>
                      updateSelected({ ...selected, label: e.target.value })
                    }
                  />
                </td>
              </tr>

              <tr>
                <th scope="row">Icon Name</th>
                <td>
                  <Input
                    placeholder="Eg - EditOutlined/HistoryOutlined"
                    value={selected.icon}
                    onChange={(e) =>
                      updateSelected({ ...selected, icon: e.target.value })
                    }
                  />
                  <span>
                    <strong>Refer:</strong>{" "}
                    <a
                      href="https://ant.design/components/icon"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      List of icons
                    </a>
                  </span>
                </td>
              </tr>

              <tr>
                <th scope="row">Class Name</th>
                <td>
                  <Input
                    placeholder="Button class name eg: btn-submit"
                    value={selected.className}
                    onChange={(e) =>
                      updateSelected({ ...selected, className: e.target.value })
                    }
                  />
                </td>
              </tr>

              <tr>
                <th scope="row">Button Type</th>
                <td>
                  <Input
                    value={selected.type}
                    onChange={(e) =>
                      updateSelected({ ...selected, type: e.target.value })
                    }
                    disabled
                  />
                </td>
              </tr>

              <tr>
                <th scope="row">Action Mode</th>
                <td>
                  <Select
                    value={selected.mode}
                    onChange={handleActionModeChange}
                    style={{ width: "100%" }}
                  >
                    <Option value="">Select</Option>
                    <Option value="Create">Create</Option>
                    <Option value="Edit">Edit</Option>
                    <Option value="All">All</Option>
                  </Select>
                  <span>
                    <strong>Note:</strong> Whether the button is visible on
                    Create, Edit, or All Mode
                  </span>
                </td>
              </tr>

              <tr>
                <th scope="row">Action Type</th>
                <td>
                  <Input
                    placeholder="System defined Action Type: DynamicFormActionSubmit / DynamicFormActionFetch / DynamicFormActionUpdate"
                    value={selected.actionType}
                    onChange={(e) =>
                      updateSelected({
                        ...selected,
                        actionType: e.target.value,
                      })
                    }
                  />
                  <span>
                    <strong>Note:</strong> System defined Action Type:
                    DynamicFormActionSubmit / DynamicFormActionFetch /
                    DynamicFormActionUpdate
                  </span>
                </td>
              </tr>
            </tbody>
          </table>

          <h4>Actions</h4>

          {selected.actions.map((act, i) => (
            <div
              key={i} // NOSONAR : ignore
              style={{ border: "1px dashed gray", padding: 5, marginBottom: 5 }}
            >
              <Flex gap="small" wrap>
                <Button
                  onClick={() => moveActionUp(i)}
                  color="geekblue"
                  variant="dashed"
                  size="small"
                  icon={<UpOutlined />}
                />
                <Button
                  onClick={() => moveActionDown(i)}
                  color="magenta"
                  variant="dashed"
                  size="small"
                  icon={<DownOutlined />}
                />
              </Flex>

              <label htmlFor="ActionId">Action ID:</label>
              <Input
                id="ActionId"
                placeholder="Should be unique for each action"
                value={act.actionID}
                onChange={(e) => {
                  const updatedActions = selected.actions.map((action, idx) => {
                    if (idx === i) {
                      return { ...action, actionID: e.target.value };
                    }
                    return action;
                  });
                  updateSelected({ ...selected, actions: updatedActions });
                }}
              />

              <label>
                Execution Type:{" "}
                <span className="text-danger">
                  (Select whether to execute SP/Query or Function)
                </span>
              </label>
              <Select
                value={selected.actions[0]?.execType} // Assuming actions are non-empty
                onChange={(value) => handleExecTypeChange(value, 0)} // Change for first action (can be customized)
                style={{ width: "100%" }}
              >
                <Select.Option value="">Select</Select.Option>
                <Select.Option value="SP">SP</Select.Option>
                <Select.Option value="Query">Query</Select.Option>
                <Select.Option value="Function">Function</Select.Option>
              </Select>

              <label htmlFor="ExecData">Execution Data:</label>
              <Input
                id="ExecData"
                placeholder="Mention either function name, stored procedure name or query basis the execution type"
                value={act.execData}
                onChange={(e) => {
                  const updatedActions = selected.actions.map((action, idx) => {
                    if (idx === i) {
                      return { ...action, execData: e.target.value };
                    }
                    return action;
                  });
                  updateSelected({ ...selected, actions: updatedActions });
                }}
              />

              <div style={{ marginTop: 5 }}>
                <label htmlFor="execParam">Execution Parameters:</label>
                {act.execParams.map((param, paramIndex) => (
                  <Input
                    name="execParam"
                    key={paramIndex} // NOSONAR : igonre
                    placeholder="Parameters to be sent to function. Parameter name should match field backend name"
                    value={param.name}
                    onChange={(e) =>
                      handleParamNameChange(i, paramIndex, e.target.value)
                    }
                  />
                ))}

                <Button
                  onClick={() => {
                    const updatedActions = selected.actions.map(
                      (action, idx) => {
                        if (idx === i) {
                          return {
                            ...action,
                            execParams: [...action.execParams, { name: "" }],
                          };
                        }
                        return action;
                      }
                    );
                    updateSelected({ ...selected, actions: updatedActions });
                  }}
                  color="purple"
                  variant="dashed"
                  size="small"
                  icon={<PlusOutlined />}
                >
                  Param
                </Button>
              </div>
            </div>
          ))}

          <Button
            onClick={() => {
              const newAction: Action = {
                actionID: "",
                execType: "",
                execData: "",
                execParams: [],
              };
              updateSelected({
                ...selected,
                actions: [...selected.actions, newAction],
              });
            }}
            color="cyan"
            variant="solid"
            icon={<PlusCircleOutlined />}
          >
            Add New Action
          </Button>
        </div>
      )}
    </div>
  );
};

export default ReportActionConfig;
